import React, { useState } from 'react';
import { TextInput, View, StyleSheet, TextInputProps, StyleProp, ViewStyle, TextStyle } from 'react-native';
import { Theme } from '../../utils/theme';
import Text from './Text';

interface InputProps extends Omit<TextInputProps, 'style'> {
  label?: string;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
  inputStyle?: StyleProp<TextStyle>;
  containerStyle?: StyleProp<ViewStyle>;
}

export default function Input({ 
  label,
  error = false, 
  errorMessage,
  disabled = false, 
  style, 
  inputStyle,
  containerStyle,
  onFocus,
  onBlur,
  multiline = false,
  ...props 
}: InputProps) {
  const [focused, setFocused] = useState(false);

  const handleFocus = (e: any) => {
    setFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: any) => {
    setFocused(false);
    onBlur?.(e);
  };

  const inputContainerStyle: StyleProp<ViewStyle> = [
    styles.container,
    focused && styles.containerFocused,
    error && styles.containerError,
    disabled && styles.containerDisabled,
    containerStyle,
  ];

  const textInputStyle: StyleProp<TextStyle> = [
    styles.input,
    multiline && styles.inputMultiline,
    disabled && styles.inputDisabled,
    inputStyle,
  ];

  return (
    <View style={style}>
      {label && (
        <Text variant="body2" style={styles.label}>
          {label}
        </Text>
      )}
      <View style={inputContainerStyle}>
        <TextInput
          style={textInputStyle}
          placeholderTextColor={Theme.colors.textDisabled}
          editable={!disabled}
          multiline={multiline}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...props}
        />
      </View>
      {error && errorMessage && (
        <Text variant="caption" style={styles.errorMessage}>
          {errorMessage}
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: Theme.borderRadius.md,
    borderWidth: 1,
    borderColor: Theme.colors.border,
    backgroundColor: Theme.colors.surface,
    paddingHorizontal: Theme.spacing.lg,
    paddingVertical: Theme.spacing.md,
    minHeight: 56,
  },
  containerFocused: {
    borderColor: Theme.colors.primary,
    borderWidth: 2,
  },
  containerError: {
    borderColor: Theme.colors.error,
  },
  containerDisabled: {
    opacity: 0.6,
  },
  input: {
    flex: 1,
    fontSize: 16,
    lineHeight: 24,
    color: Theme.colors.textPrimary,
    fontFamily: Theme.typography.fontFamily.regular,
    paddingVertical: 0,
  },
  inputMultiline: {
    minHeight: 80,
    textAlignVertical: 'top',
    paddingTop: Theme.spacing.sm,
  },
  inputDisabled: {
    color: Theme.colors.textDisabled,
  },
  label: {
    marginBottom: Theme.spacing.sm,
  },
  errorMessage: {
    marginTop: Theme.spacing.xs,
    color: Theme.colors.error,
  },
}); 